import { useCallback, useEffect, useState, useRef } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../../app/itemGridPreferencesSlice";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import { SortOrderDropDownSelection } from "../../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";
import CustomerGroupModel from "./customerGroupModel";
import showErrorToast, {
  showSuccessToast,
} from "../../../components/CustomComponents/toast/toast";
import "../../../components/CustomComponents/footer/footer.css";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../../utils/managePageConstants";
import "./../../order/ManageOrderFilters.css";

const ManageCustomersGroup = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "customerGroups";
  const defaultVisibleColumns = {
    description: true,
  };
  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "ID", label: "ID" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const sortByOptions = [
    { value: "ID", label: "ID" },
    { value: "Name", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const fetchCustomerGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CUSTOMERS_GROUP, {
        params: {
          CompID,
          Search: search || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
        },
      });

      const groupData = response.data.data || [];
      if (Array.isArray(groupData)) {
        setCustomerGroups(groupData);
      } else {
        setCustomerGroups([]);
      }
    } catch (error) {
      setCustomerGroups([]);
      showErrorToast("Failed to fetch customer groups.");
    } finally {
      setLoading(false);
    }
  }, [CompID, search, sortBy, sortOrder]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCustomerGroups();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchCustomerGroups]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handleGroupSubmit = async (groupData, isUpdate) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_CUSTOMER_GROUP,
        groupData
      );
      if (response.data.status) {
        const updatedGroup = response.data.data;

        if (isUpdate) {
          setCustomerGroups((prev) =>
            prev.map((group) =>
              group.id === updatedGroup.id ? updatedGroup : group
            )
          );
        } else {
          setCustomerGroups((prev) => [...prev, updatedGroup]);
        }

        showSuccessToast(
          `Customer Type Group is ${isUpdate ? "Updated" : "Added"}.`
        );
      } else {
        showErrorToast(response.data.message || "Operation failed.");
      }
    } catch (error) {
      showErrorToast("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroupClick = () => {
    setGroupToEdit(null);
    setShowAddGroupModal(true);
  };

  const handleEditGroupClick = (group) => {
    setGroupToEdit(group);
    setShowAddGroupModal(true);
  };

  const DeleteCustomerTypeGroup = async (customerGroup) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${customerGroup.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance
        .delete(APIs.DELETE_CUSTOMER_GROUP_TYPE, {
          params: {
            CompID,
            ID: customerGroup.id,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setCustomerGroups((prevGroups) =>
              prevGroups.filter((delGroup) => delGroup.id !== customerGroup.id)
            );
            showSuccessToast("Customer Type Group is Deleted.");
          } else showErrorToast(response.data.message);
        });
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (groupId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (customerGroups[i]?.id) {
            newSelected.add(customerGroups[i].id);
          }
        }
      } else {
        if (newSelected.has(groupId)) {
          newSelected.delete(groupId);
        } else {
          newSelected.add(groupId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(customerGroups.map((item) => item.id));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customer groups selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected customer groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((groupId) =>
          axiosInstance.delete(APIs.DELETE_CUSTOMER_GROUP_TYPE, {
            params: {
              CompID,
              ID: groupId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchCustomerGroups();
      showSuccessToast(
        `${selectedItems.size} customer groups deleted successfully.`
      );
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to delete selected customer groups.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No customer groups selected for editing.");
      return;
    }

    // For bulk edit, pass selected IDs to the modal or a new bulk edit component
    const selectedGroups = customerGroups.filter((group) =>
      selectedItems.has(group.id)
    );
    setGroupToEdit(selectedGroups); // Pass array of selected groups
    setShowAddGroupModal(true); // Reuse modal for bulk edit
  };

  // Function to truncate description text
  const truncateDescription = (description) => {
    if (!description) return "";
    return description.length > 50
      ? `${description.substring(0, 50)}...`
      : description;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Customer Group</li>
          </ol>
          <h1 className="page-header mb-0">Manage Customer Group</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleAddGroupClick}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              boxShadow: `0 2px 8px ${themeColor}40`,
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Customer Group
          </button>
        </div>
      </div>
      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Info and Actions (Shown only when groups are selected) */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} group{selectedItems.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleBulkEdit}
                        title="Edit Selected Groups"
                      >
                        <i className="fa fa-edit me-1"></i>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Groups"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            customerGroups.length > 0 &&
                            selectedItems.size === customerGroups.length
                          }
                        />
                      </th>
                      <th className="pt-2 pb-2">Name</th>
                      {visibleColumns.description && (
                        <th className="pt-2 pb-2">Description</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="ms-2">Loading...</span>
                        </td>
                      </tr>
                    ) : customerGroups.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fa fa-search fa-2x mb-2"></i>
                            <p className="mb-0">
                              {search.trim() ? "No customer groups found matching your search criteria." : "No customer groups available."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      customerGroups.map((group, index) => (
                        <tr key={group.id}>
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(group.id)}
                              onChange={(e) => handleSelectItem(group.id, index, e)}
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => handleEditGroupClick(group)}
                            style={{ cursor: "pointer" }}
                          >
                            {group.name}
                          </td>
                          {visibleColumns.description && (
                            <td
                              className="align-middle"
                              onClick={() => handleEditGroupClick(group)}
                              style={{ cursor: "pointer" }}
                            >
                              {truncateDescription(group.description)}
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Edit Icon */}
                              <button
                                onClick={() => handleEditGroupClick(group)}
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Customer Group"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = themeColor;
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                              </button>

                              {/* Delete Icon */}
                              <button
                                onClick={() => DeleteCustomerTypeGroup(group)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Customer Group"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomerGroupModel
        isModalOpen={showAddGroupModal}
        setIsModalOpen={setShowAddGroupModal}
        onGroupSubmit={handleGroupSubmit}
        compID={CompID}
        groupToEdit={groupToEdit}
      />
    </div>
  );
};

export default ManageCustomersGroup;